import { z } from 'zod';

export const BILL_STATUSES = ['open', 'partial', 'paid', 'void'] as const;
export type BillStatus = (typeof BILL_STATUSES)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const nonNegMoney = z
  .string()
  .regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative money value');
const positiveMoney = nonNegMoney.refine((v) => Number(v) > 0, 'must be greater than zero');

export const billLineInputSchema = z.object({
  accountId: z.string().uuid(), // expense/asset account debited
  description: z.string().max(500).nullable().optional(),
  quantity: nonNegMoney.default('1'),
  unitPrice: nonNegMoney.default('0'),
  locationId: z.string().uuid().nullable().optional(),
  classId: z.string().uuid().nullable().optional(),
});
export type BillLineInput = z.infer<typeof billLineInputSchema>;

export const createBillInputSchema = z.object({
  vendorId: z.string().uuid(),
  /** Optional internal number; auto-assigned (BILL-00001…) when omitted. */
  billNumber: z.string().trim().min(1).max(20).optional(),
  /** The vendor's own invoice/reference number. */
  vendorReference: z.string().trim().max(100).nullable().optional(),
  billDate: isoDate,
  dueDate: isoDate,
  terms: z.string().max(100).nullable().optional(),
  memo: z.string().max(500).nullable().optional(),
  lines: z.array(billLineInputSchema).min(1),
});
export type CreateBillInput = z.infer<typeof createBillInputSchema>;

/** Edit an open (unpaid) bill. The bill number is fixed; everything else may change. */
export const updateBillInputSchema = z.object({
  vendorId: z.string().uuid(),
  vendorReference: z.string().trim().max(100).nullable().optional(),
  billDate: isoDate,
  dueDate: isoDate,
  terms: z.string().max(100).nullable().optional(),
  memo: z.string().max(500).nullable().optional(),
  lines: z.array(billLineInputSchema).min(1),
});
export type UpdateBillInput = z.infer<typeof updateBillInputSchema>;

export const recordBillPaymentInputSchema = z.object({
  paymentDate: isoDate,
  amount: positiveMoney,
  reference: z.string().max(100).nullable().optional(),
  /** GL bank/cash account to credit (the account the bill is paid from). */
  paymentAccountId: z.string().uuid(),
  /** Set after the user confirms a possible-duplicate warning. */
  allowDuplicate: z.boolean().optional(),
});
export type RecordBillPaymentInput = z.infer<typeof recordBillPaymentInputSchema>;

export const voidBillInputSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type VoidBillInput = z.infer<typeof voidBillInputSchema>;

export const listBillsQuerySchema = z.object({
  vendorId: z.string().uuid().optional(),
  status: z.enum(BILL_STATUSES).optional(),
});
export type ListBillsQuery = z.infer<typeof listBillsQuerySchema>;

export interface Bill {
  id: string;
  companyId: string;
  vendorId: string;
  vendorName: string;
  billNumber: string;
  vendorReference: string | null;
  billDate: string;
  dueDate: string;
  status: BillStatus;
  /** Derived: open/partial and past the due date. */
  isOverdue: boolean;
  apAccountId: string;
  subtotal: string;
  total: string;
  amountPaid: string;
  outstandingAmount: string;
  memo: string | null;
  terms: string | null;
  journalEntryId: string | null;
  voidReason: string | null;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BillLine {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  description: string | null;
  quantity: string;
  unitPrice: string;
  amount: string;
}

export interface BillPayment {
  id: string;
  paymentDate: string;
  amount: string;
  paymentAccountId: string;
  paymentAccountCode: string;
  paymentAccountName: string;
  reference: string | null;
  journalEntryId: string | null;
  createdAt: string;
}

export interface BillWithDetails extends Bill {
  lines: BillLine[];
  payments: BillPayment[];
}

export interface NextBillNumber {
  billNumber: string;
}

// ── Vendor center ───────────────────────────────────────────────────────────

export interface VendorCenterRow {
  vendorId: string;
  name: string;
  email: string | null;
  billCount: number;
  openCount: number;
  overdueCount: number;
  totalBilled: string;
  totalPaid: string;
  outstanding: string;
  overdue: string;
}

export interface VendorCenterResponse {
  rows: VendorCenterRow[];
  totals: {
    billCount: number;
    totalBilled: string;
    outstanding: string;
    overdue: string;
  };
}

// ── A/P aging ───────────────────────────────────────────────────────────────

export interface ApAgingRow {
  vendorId: string;
  name: string;
  current: string;
  d1_30: string;
  d31_60: string;
  d61_90: string;
  d91_plus: string;
  total: string;
}

export interface ApAgingResponse {
  asOfDate: string;
  rows: ApAgingRow[];
  totals: {
    current: string;
    d1_30: string;
    d31_60: string;
    d61_90: string;
    d91_plus: string;
    total: string;
  };
}
